import React, { useState, useEffect, useRef } from "react";
import {
  ChevronLeft,
  Check,
  TrendingUp,
  Plus,
  X,
} from "lucide-react";
import { ReactComponent as Star } from "../../assets/icons/svg/triva_premium-icon.svg";
import { ReactComponent as PenIcon } from "../../assets/icons/svg/pen.svg";
import { ReactComponent as UploadIcon } from "../../assets/icons/svg/Upload icon.svg";
import { ReactComponent as ChevronDown } from "../../assets/icons/svg/Polygon-down.svg";
import { ReactComponent as ChevronUp } from "../../assets/icons/svg/Polygon-up.svg";
import InAppWebView from "./InAppWebView";
import GradientBorderPremiumTag from "../ui/Gradient_Border_Premium_Tag";
import MedicalConditionPopup from "../ui/Medical_Condition_Popup";
import API_URLS from "../api/urls";
import PremiumPlanSuccessPage from "./Premium_Plan_Success_Page";
import { AuthStorage } from "../desktop_screens/auth/AuthStorage";
import {
  SIGNATURE_PLAN_PERFECT_FOR,
  SIGNATURE_PLAN_WHAT_YOU_GET,
  SIGNATURE_PLAN_KNOW_MORE_URL,
} from "../data/signaturePlanData";

interface PremiumSignaturePlanDetailsProps {
  userId: number | null;
  authToken?: string | null;
  makeAuthenticatedApiCall?: (
    url: string,
    options?: RequestInit,
  ) => Promise<Response>;
  onBack: () => void;
}

// Interface for user personal plan data
interface UserPersonalPlanData {
  email?: string;
  phone?: string;
  countryCode?: string;
  bestTime?: string;
  profession?: string;
  location?: string;
  heightValue?: string;
  heightUnit?: string;
  heightInches?: string;
  weightValue?: string;
  weightUnit?: string;
  foodPreference?: string;
  medicalConditions?: string[];
  goal?: string;
  conditionDetails?: Record<string, string>;
  user_id?: number;
  user_email?: string;
  user_mobileno?: string;
  user_fname?: string;
  user_lname?: string;
  age?: number;
  city?: string;
  state?: string;
  dob?: string;
  gender?: string;
  height_value?: number;
  height_parameter?: string;
  height_inches?: string;
  weight_value?: number;
  weight_parameter?: string;
  food_preference?: string;
  medical_conditions?: string;
  medical_information?: string;
  target_weight?: number;
  target_weight_parameter?: string;
  nutritionist_id?: string;
  fcm_token?: string;
  profile_url?: string;
  alcohol?: string;
  supplements_intake?: string;
  progress?: string;
  last_status?: string;
  user_apple_id?: string;
  otp?: string;
}

const convertDecimalFeetToFeetInches = (decimalFeet: number) => {
  const feet = Math.floor(decimalFeet);
  const inches = Math.round((decimalFeet - feet) * 12);
  return { feet: feet.toString(), inches: inches.toString() };
};

const convertCmToFeetInches = (cm: number) => {
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return { feet: feet.toString(), inches: inches.toString() };
};

const convertFeetInchesToCm = (feet: string, inches: string) => {
  const totalInches = (parseInt(feet) || 0) * 12 + (parseInt(inches) || 0);
  return Math.round(totalInches * 2.54).toString();
};

const convertKgToLbs = (kg: number) => {
  return Math.round(kg * 2.20462).toString();
};

const convertLbsToKg = (lbs: number) => {
  return Math.round(lbs / 2.20462).toString();
};

/**
 * ✅ FIXED: Helper function to make authenticated API calls
 * Prioritizes AuthStorage if authenticated, otherwise uses makeAuthenticatedApiCall prop
 */
const makeAuthenticatedRequest = async (
  url: string,
  options: RequestInit = {},
  makeAuthenticatedApiCall?: (
    url: string,
    options?: RequestInit,
  ) => Promise<Response>,
) => {
  // Check if AuthStorage has authentication
  const hasAuthStorage = AuthStorage.isAuthenticated();
  const token = AuthStorage.getAccessToken();

  if (hasAuthStorage && token) {
    // Use AuthStorage token (preferred method when user is logged in)
    console.log("✅ Using AuthStorage token for authentication");

    const headers: HeadersInit = {
      Authorization: `Bearer ${token}`,
    };

    // Only add Content-Type for non-FormData requests
    if (!(options.body instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }

    return fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...(options.headers || {}),
      },
    });
  }

  // Fallback: use makeAuthenticatedApiCall from props (for legacy flow)
  if (makeAuthenticatedApiCall) {
    console.log(
      "Using makeAuthenticatedApiCall from props (no AuthStorage available)",
    );
    return makeAuthenticatedApiCall(url, options);
  }

  // No authentication available
  throw new Error("No authentication token available");
};

const PremiumSignaturePlanDetails: React.FC<
  PremiumSignaturePlanDetailsProps
> = ({ userId, authToken, makeAuthenticatedApiCall, onBack }) => {
  const [showForm, setShowForm] = useState(false);
  const [showWebView, setShowWebView] = useState(false);
  const [showMedicalPopup, setShowMedicalPopup] = useState(false);
  const [selectedCondition, setSelectedCondition] = useState("");
  const [conditionDetails, setConditionDetails] = useState<
    Record<string, string>
  >({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingUserData, setIsLoadingUserData] = useState(false);
  const [fileUploadError, setFileUploadError] = useState("");
  const [showSuccessPage, setShowSuccessPage] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    countryCode: "91",
    bestTime: "",
    profession: "",
    location: "",
    heightValue: "",
    heightUnit: "ft",
    heightInches: "",
    weightValue: "",
    weightUnit: "kg",
    foodPreference: "",
    medicalConditions: [] as string[],
    goal: "",
    medicalReport: null as File | null,
  });

  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  const dropdownRefs = {
    food: useRef<HTMLDivElement>(null),
    time: useRef<HTMLDivElement>(null),
    height: useRef<HTMLDivElement>(null),
    weight: useRef<HTMLDivElement>(null),
    phone: useRef<HTMLDivElement>(null),
  };

  // ✅ FIXED: Updated fetchUserPersonalPlan function
  const fetchUserPersonalPlan = async () => {
    const currentUserId = userId || AuthStorage.getUserId();

    if (!currentUserId) {
      console.log("Cannot fetch user data: Missing userId");
      return;
    }

    setIsLoadingUserData(true);

    try {
      console.log("Fetching user personal plan data for user:", currentUserId);

      // ✅ FIXED: Use helper function that supports both auth methods
      const response = await makeAuthenticatedRequest(
        API_URLS.GET_USER_PERSONAL_PLAN(currentUserId),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
        makeAuthenticatedApiCall,
      );

      if (response.ok) {
        const result = await response.json();
        console.log("User personal plan data received:", result);

        let userData: UserPersonalPlanData = {};

        if (result.user) {
          userData = result.user;
        } else if (result.data) {
          userData = result.data;
        } else {
          userData = result;
        }

        console.log("Extracted user data:", userData);

        setFormData((prevData) => {
          let medicalConditionsArray: string[] = [];
          try {
            if (userData.medical_conditions) {
              let conditionsStr = userData.medical_conditions;
              if (typeof conditionsStr === "string") {
                conditionsStr = conditionsStr.replace(/[\[\]"]/g, "");
                medicalConditionsArray = conditionsStr
                  .split(",")
                  .map((item) => item.trim())
                  .filter((item) => item);
              }
            }
          } catch (error) {
            console.log("Error parsing medical conditions:", error);
            medicalConditionsArray = userData.medical_conditions
              ? [String(userData.medical_conditions)]
              : [];
          }

          let phoneNumber = "";
          let countryCode = "91";
          if (userData.user_mobileno) {
            const fullPhone = userData.user_mobileno.replace(/^\+/, "");
            if (fullPhone.startsWith("91")) {
              countryCode = "91";
              phoneNumber = fullPhone.substring(2);
            } else {
              phoneNumber = fullPhone;
            }
          }

          let heightValue = "";
          let heightUnit = "ft";
          let heightInches = "";

          if (userData.height_value) {
            if (
              userData.height_parameter === "Cm" ||
              userData.height_parameter === "cm"
            ) {
              heightUnit = "cm";
              heightValue = String(userData.height_value);
              heightInches = "";
            } else {
              heightUnit = "ft";
              const { feet, inches } = convertDecimalFeetToFeetInches(
                userData.height_value,
              );
              heightValue = feet;
              heightInches = inches;
            }
          }

          let weightValue = "";
          let weightUnit = "kg";

          if (userData.weight_value) {
            weightUnit =
              userData.weight_parameter === "Lbs" ||
              userData.weight_parameter === "lbs"
                ? "lbs"
                : "kg";
            weightValue = String(userData.weight_value);
          }

          const newFormData = {
            ...prevData,
            email: userData.user_email || prevData.email,
            phone: phoneNumber || prevData.phone,
            countryCode: countryCode || prevData.countryCode,
            bestTime: userData.bestTime || prevData.bestTime,
            profession: userData.profession || prevData.profession,
            location: userData.city || prevData.location,
            heightValue: heightValue || prevData.heightValue,
            heightUnit: heightUnit,
            heightInches: heightInches || prevData.heightInches,
            weightValue: weightValue || prevData.weightValue,
            weightUnit: weightUnit,
            foodPreference: userData.food_preference || prevData.foodPreference,
            medicalConditions: medicalConditionsArray,
            goal: userData.goal || prevData.goal,
          };

          console.log("New form data being set:", newFormData);
          return newFormData;
        });

        if (userData.conditionDetails) {
          setConditionDetails(userData.conditionDetails);
          console.log("Condition details set:", userData.conditionDetails);
        }

        console.log("Form auto-filled with user data");
      } else {
        console.error("Failed to fetch user data. Status:", response.status);
        const errorText = await response.text();
        console.error("Error response:", errorText);
      }
    } catch (error) {
      console.error("Error fetching user personal plan:", error);
    } finally {
      setIsLoadingUserData(false);
    }
  };

  const handleHeightUnitChange = (newUnit: string) => {
    setFormData((prev) => {
      let newHeightValue = "";
      let newHeightInches = "";

      if (prev.heightValue && prev.heightUnit !== newUnit) {
        if (prev.heightUnit === "ft" && newUnit === "cm") {
          const cm = convertFeetInchesToCm(prev.heightValue, prev.heightInches);
          newHeightValue = cm;
          newHeightInches = "";
        } else if (prev.heightUnit === "cm" && newUnit === "ft") {
          const { feet, inches } = convertCmToFeetInches(
            parseInt(prev.heightValue),
          );
          newHeightValue = feet;
          newHeightInches = inches;
        }
      }

      return {
        ...prev,
        heightUnit: newUnit,
        heightValue: newHeightValue,
        heightInches: newHeightInches,
      };
    });
  };

  const handleWeightUnitChange = (newUnit: string) => {
    setFormData((prev) => {
      let newWeightValue = "";

      if (prev.weightValue && prev.weightUnit !== newUnit) {
        if (prev.weightUnit === "kg" && newUnit === "lbs") {
          newWeightValue = convertKgToLbs(parseInt(prev.weightValue));
        } else if (prev.weightUnit === "lbs" && newUnit === "kg") {
          newWeightValue = convertLbsToKg(parseInt(prev.weightValue));
        }
      }

      return {
        ...prev,
        weightUnit: newUnit,
        weightValue: newWeightValue,
      };
    });
  };

  useEffect(() => {
    console.log(
      "PremiumSignaturePlanDetails - Auth token available:",
      !!authToken,
    );
    console.log(
      "PremiumSignaturePlanDetails - makeAuthenticatedApiCall available:",
      !!makeAuthenticatedApiCall,
    );
    console.log("PremiumSignaturePlanDetails - User ID:", userId);
    console.log(
      "PremiumSignaturePlanDetails - AuthStorage debug:",
      AuthStorage.getDebugInfo(),
    );
  }, [authToken, makeAuthenticatedApiCall, userId]);

  // ✅ ADDED: Scroll to top whenever showForm changes
  useEffect(() => {
    if (showForm) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [showForm]);

  const isFormValid = () => {
    const requiredFields = [
      formData.email,
      formData.phone,
      formData.bestTime,
      formData.profession,
      formData.location,
      formData.heightValue,
      formData.weightValue,
      formData.foodPreference,
      formData.goal,
    ];

    const allFieldsFilled = requiredFields.every(
      (field) => field.trim() !== "",
    );
    const medicalConditionSelected = formData.medicalConditions.length > 0;
    const heightValid =
      formData.heightUnit === "ft" ? formData.heightInches.trim() !== "" : true;
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
    const phoneValid = formData.phone.replace(/\D/g, "").length >= 10;

    return (
      allFieldsFilled &&
      medicalConditionSelected &&
      heightValid &&
      emailValid &&
      phoneValid
    );
  };

  const validateField = (fieldName: string, value: string) => {
    let error = "";

    switch (fieldName) {
      case "email":
        if (!value.trim()) error = "Email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          error = "Invalid email format";
        break;
      case "phone":
        if (!value.trim()) error = "Phone number is required";
        else if (value.replace(/\D/g, "").length < 10)
          error = "Phone number must be at least 10 digits";
        break;
      case "profession":
        if (!value.trim()) error = "Profession is required";
        break;
      case "location":
        if (!value.trim()) error = "Location is required";
        break;
      case "heightValue":
        if (!value.trim()) error = "Height is required";
        break;
      case "weightValue":
        if (!value.trim()) error = "Weight is required";
        break;
      case "goal":
        if (!value.trim()) error = "Goal description is required";
        break;
      default:
        break;
    }

    setFieldErrors((prev) => ({ ...prev, [fieldName]: error }));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideAllDropdowns = Object.values(dropdownRefs).every(
        (ref) => ref.current && !ref.current.contains(target),
      );

      if (isOutsideAllDropdowns) {
        setActiveDropdown(null);
      }
    };

    if (activeDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeDropdown]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get("status");

    if (status === "premium-success") {
      setShowSuccessPage(true);
      setShowForm(false);
    }

    const handlePopState = (event: PopStateEvent) => {
      if (event.state?.showSuccessPage) {
        setShowSuccessPage(true);
        setShowForm(false);
      } else {
        setShowSuccessPage(false);
        setShowForm(false);
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const toggleDropdown = (dropdownName: string) => {
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
  };

  const closeDropdown = () => {
    setActiveDropdown(null);
  };

  const handleKnowMore = () => {
    console.log("Know more about Deepika Chalasani clicked");
    setShowWebView(true);
  };

  const handleCloseWebView = () => {
    setShowWebView(false);
  };

  const handleConnectWithExperts = async () => {
    setShowForm(true);

    // ✅ ADDED: Scroll to top when form is shown
    window.scrollTo({ top: 0, behavior: "smooth" });

    // ✅ Check both userId prop and AuthStorage
    if (userId || AuthStorage.getUserId()) {
      await fetchUserPersonalPlan();
    }
  };

  const handleBackToDetails = () => {
    setShowForm(false);

    // ✅ ADDED: Scroll to top when going back to details
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNavigateFromSuccess = () => {
    const currentUrl = window.location.pathname;
    window.history.replaceState(null, "", currentUrl);

    setShowSuccessPage(false);
    setShowForm(false);
    onBack();
  };

  // ✅ FIXED: Updated handleFormSubmit function
  const handleFormSubmit = async () => {
    if (!isFormValid()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const currentUserId = userId || AuthStorage.getUserId();

      console.log("Debug User ID info:");
      console.log("- Props userId:", userId);
      console.log("- AuthStorage getUserId():", AuthStorage.getUserId());
      console.log("- Final currentUserId:", currentUserId);

      if (!currentUserId) {
        alert("User ID not found. Please log in again.");
        setIsSubmitting(false);
        return;
      }

      let heightValue = 0;
      let heightParameter = formData.heightUnit === "ft" ? "ft" : "cm";

      if (formData.heightUnit === "ft") {
        const feet = parseFloat(formData.heightValue) || 0;
        const inches = parseFloat(formData.heightInches) || 0;
        heightValue = parseFloat((feet + inches / 12).toFixed(1));
      } else {
        heightValue = parseFloat(
          parseFloat(formData.heightValue || "0").toFixed(1),
        );
      }

      const weightValue = parseFloat(
        parseFloat(formData.weightValue || "0").toFixed(1),
      );
      const weightParameter = formData.weightUnit;

      let medicalInformation = "";
      const selectedConditions = formData.medicalConditions.filter(
        (condition) => condition !== "None",
      );

      if (selectedConditions.length > 0) {
        medicalInformation = selectedConditions
          .map((condition) => {
            const details = conditionDetails[condition];
            return details ? `${condition}: ${details}` : condition;
          })
          .join(", ");
      }

      const mobileNumber = `+${formData.countryCode}${formData.phone}`;

      const formDataToSend = new FormData();

      formDataToSend.append("fk_user_id", currentUserId.toString());
      formDataToSend.append("best_time_to_connect", formData.bestTime);
      formDataToSend.append("what_help_you_are_looking_from_us", formData.goal);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("mobile_number", mobileNumber);
      formDataToSend.append("profession", formData.profession);
      formDataToSend.append("location", formData.location);
      formDataToSend.append("height_parameter", heightParameter);
      formDataToSend.append("height_value", heightValue.toString());
      formDataToSend.append("weight_parameter", weightParameter);
      formDataToSend.append("weight_value", weightValue.toString());
      formDataToSend.append("food_preference", formData.foodPreference);
      formDataToSend.append(
        "medical_conditions",
        JSON.stringify(formData.medicalConditions),
      );
      formDataToSend.append("medical_information", medicalInformation);

      if (formData.medicalReport) {
        formDataToSend.append("medical_report", formData.medicalReport);
      }

      console.log("Premium plan form submitted as FormData");
      console.log("FormData contents:");
      formDataToSend.forEach((value, key) => {
        console.log(
          `${key}: ${value instanceof File ? `File: ${value.name}` : value}`,
        );
      });

      // ✅ FIXED: Use helper function that supports both auth methods
      const response = await makeAuthenticatedRequest(
        API_URLS.UPDATE_PERSONAL_PLAN_REQUEST,
        {
          method: "POST",
          body: formDataToSend,
        },
        makeAuthenticatedApiCall,
      );

      console.log("Response status:", response.status);
      console.log("Response headers:", response.headers);

      if (response.ok) {
        const result = await response.json();
        console.log("Premium plan submission response:", result);

        if (result.error) {
          console.error("Backend returned error:", result.error);
          alert(`Submission failed: ${result.error}`);
          return;
        }

        console.log("Submission successful!");
        setShowSuccessPage(true);

        const currentUrl = window.location.pathname;
        const newUrl = `${currentUrl}?status=premium-success`;

        window.history.pushState(
          {
            showSuccessPage: true,
            planType: "premium",
            submissionTime: Date.now(),
          },
          "",
          newUrl,
        );

        window.scrollTo(0, 0);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("API response error:", errorData);
        throw new Error(`Server responded with status: ${response.status}`);
      }
    } catch (error) {
      console.error("Form submission error:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMedicalConditionToggle = (condition: string) => {
    if (condition === "None") {
      setFormData((prev) => ({
        ...prev,
        medicalConditions: prev.medicalConditions.includes("None")
          ? []
          : ["None"],
      }));
      if (!formData.medicalConditions.includes("None")) {
        setConditionDetails({});
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        medicalConditions: prev.medicalConditions.includes("None")
          ? [condition]
          : prev.medicalConditions.includes(condition)
            ? prev.medicalConditions.filter((c) => c !== condition)
            : [...prev.medicalConditions, condition],
      }));
    }
  };

  const handleConditionEdit = (condition: string) => {
    setSelectedCondition(condition);
    setShowMedicalPopup(true);
  };

  const handleConditionDetailsSave = (condition: string, details: string) => {
    setConditionDetails((prev) => ({
      ...prev,
      [condition]: details,
    }));
    setShowMedicalPopup(false);
  };

  const handleCloseMedicalPopup = () => {
    setShowMedicalPopup(false);
    setSelectedCondition("");
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setFileUploadError("");

    if (file) {
      if (file.type !== "application/pdf") {
        setFileUploadError("Please upload only PDF files");
        event.target.value = "";
        return;
      }

      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        setFileUploadError(
          "File size exceeds 5MB. Please upload a smaller file",
        );
        event.target.value = "";
        return;
      }

      setFormData((prev) => ({ ...prev, medicalReport: file }));
    }
  };

  const removeFile = () => {
    setFormData((prev) => ({ ...prev, medicalReport: null }));
    setFileUploadError("");
  };

  const perfectFor = SIGNATURE_PLAN_PERFECT_FOR;
  const whatYouGet = SIGNATURE_PLAN_WHAT_YOU_GET;

  const timeOptions = [
    "Morning (9am-1pm)",
    "Afternoon (1pm-4pm)",
    "Evening (4pm-9pm)",
  ];
  const foodOptions = [
    "Vegan",
    "Vegetarian",
    "Eggetarian",
    "Pollotarian",
    "Pescatarian",
    "Carnitarian",
    "Non Vegetarian",
  ];
  const countryCodeOptions = [
    "91",
    "1",
    "44",
    "61",
    "81",
    "86",
    "33",
    "49",
    "39",
    "34",
  ];
  const medicalConditions = [
    "None",
    "PCOD",
    "PCOS",
    "Thyroid",
    "Diabetes",
    "Inflammation",
    "Gut health",
    "IBS",
    "Fatty liver",
    "Blood pressure",
    "Heart health",
    "Anemia",
    "Osteoporosis",
    "UTI",
    "Kidney disease",
    "Auto immune",
    "Food intolerance",
    "Mental health",
    "Gout",
    "Others",
  ];

  if (showWebView) {
    return (
      <InAppWebView
        url={SIGNATURE_PLAN_KNOW_MORE_URL}
        title="About Deepika Chalasani"
        onClose={handleCloseWebView}
      />
    );
  }

  if (showSuccessPage) {
    return (
      <PremiumPlanSuccessPage onNavigateHome={handleNavigateFromSuccess} />
    );
  }

  if (showForm) {
    return (
      <div className="min-h-screen bg-white px-[24px] mt-[32px]">
        {/* Header */}
        <div className="flex items-center bg-white mb-4">
          <button
            onClick={handleBackToDetails}
            className="flex items-center gap-1"
          >
            <ChevronLeft className="w-6 h-6 text-[#173430]" />
            <span className="text-[14px] font-medium text-[#202020]">Back</span>
          </button>
        </div>

        {/* Loading indicator */}
        {isLoadingUserData && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-700 text-sm">
              Loading your saved information...
            </p>
          </div>
        )}

        <div className="pb-32">
          {/* Email */}
          <div className="mb-4">
            <label className="block text-[16px] font-semibold text-[#828282] mb-2">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, email: e.target.value }));
                validateField("email", e.target.value);
              }}
              onBlur={(e) => validateField("email", e.target.value)}
              placeholder="Enter your email"
              className={`w-full px-[24px] py-[15px] text-[14px] text-[#202020] font-inter font-semibold border rounded-lg focus:outline-none focus:border-[#0DB5B5] ${
                fieldErrors.email ? "border-red-500" : "border-[#E0E0E0]"
              }`}
            />
            {fieldErrors.email && (
              <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>
            )}
          </div>

          {/* Phone Number */}
          <div className="mb-4">
            <label className="block text-[16px] font-semibold text-[#828282] mb-2">
              Phone number
            </label>
            <div className="flex">
              <div className="relative" ref={dropdownRefs.phone}>
                <button
                  onClick={() => toggleDropdown("phone")}
                  className="px-[16px] py-[15px] text-[14px] text-[#202020] font-inter font-semibold border border-[#E0E0E0] rounded-lg bg-white flex items-center gap-2 focus:outline-none focus:border-[#0DB5B5] mr-2"
                >
                  <span>{formData.countryCode}</span>
                  {activeDropdown === "phone" ? (
                    <ChevronUp className="w-2 h-2" />
                  ) : (
                    <ChevronDown className="w-2 h-2" />
                  )}
                </button>
                {activeDropdown === "phone" && (
                  <div
                    className="absolute z-10 left-0 mt-2 bg-white rounded-lg max-h-60 overflow-y-auto"
                    style={{ boxShadow: "2px 4px 16px 0px #00000026" }}
                  >
                    {countryCodeOptions.map((code) => (
                      <button
                        key={code}
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            countryCode: code,
                          }));
                          closeDropdown();
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
                      >
                        {code}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, phone: e.target.value }));
                  validateField("phone", e.target.value);
                }}
                onBlur={(e) => validateField("phone", e.target.value)}
                placeholder="Enter your phone number"
                className={`w-full px-[24px] py-[15px] text-[14px] text-[#202020] font-inter font-semibold border rounded-lg focus:outline-none focus:border-[#0DB5B5] ${
                  fieldErrors.phone ? "border-red-500" : "border-[#E0E0E0]"
                }`}
              />
            </div>
            {fieldErrors.phone && (
              <p className="text-red-500 text-xs mt-1">{fieldErrors.phone}</p>
            )}
          </div>

          {/* Best Time to Contact */}
          <div className="mb-4 relative" ref={dropdownRefs.time}>
            <label className="block text-[16px] font-semibold text-[#828282] mb-2">
              Best time to contact
            </label>
            <button
              onClick={() => toggleDropdown("time")}
              className="w-full px-[24px] py-[15px] text-[14px] text-[#202020] font-inter font-semibold border border-[#E0E0E0] bg-white rounded-lg text-left flex justify-between items-center focus:outline-none focus:border-[#0DB5B5]"
            >
              <span
                className={formData.bestTime ? "text-black" : "text-gray-400"}
              >
                {formData.bestTime || "Select time"}
              </span>
              {activeDropdown === "time" ? (
                <ChevronUp className="w-2 h-2" />
              ) : (
                <ChevronDown className="w-2 h-2" />
              )}
            </button>
            {activeDropdown === "time" && (
              <div
                className="absolute z-10 w-full mt-2 bg-white rounded-lg"
                style={{ boxShadow: "2px 4px 16px 0px #00000026" }}
              >
                {timeOptions.map((time) => (
                  <button
                    key={time}
                    onClick={() => {
                      setFormData((prev) => ({ ...prev, bestTime: time }));
                      closeDropdown();
                    }}
                    className="w-full p-3 text-[14px] text-[#000000] font-inter font-medium text-left hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
                  >
                    {time}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Profession */}
          <div className="mb-4">
            <label className="block text-[16px] font-semibold text-[#828282] mb-2">
              Profession
            </label>
            <input
              type="text"
              value={formData.profession}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  profession: e.target.value,
                }));
                validateField("profession", e.target.value);
              }}
              onBlur={(e) => validateField("profession", e.target.value)}
              placeholder="Enter profession"
              className={`w-full px-[24px] py-[15px] text-[14px] text-[#202020] font-inter font-semibold border rounded-lg focus:outline-none focus:border-[#0DB5B5] ${
                fieldErrors.profession ? "border-red-500" : "border-[#E0E0E0]"
              }`}
            />
            {fieldErrors.profession && (
              <p className="text-red-500 text-xs mt-1">
                {fieldErrors.profession}
              </p>
            )}
          </div>

          {/* Location */}
          <div className="mb-4">
            <label className="block text-[16px] font-semibold text-[#828282] mb-2">
              Location
            </label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, location: e.target.value }));
                validateField("location", e.target.value);
              }}
              onBlur={(e) => validateField("location", e.target.value)}
              placeholder="Enter your location"
              className={`w-full px-[24px] py-[15px] text-[14px] text-[#202020] font-inter font-semibold border rounded-lg focus:outline-none focus:border-[#0DB5B5] ${
                fieldErrors.location ? "border-red-500" : "border-[#E0E0E0]"
              }`}
            />
            {fieldErrors.location && (
              <p className="text-red-500 text-xs mt-1">
                {fieldErrors.location}
              </p>
            )}
          </div>

          {/* Height */}
          <div className="mb-4">
            <label className="block text-[16px] font-semibold text-[#828282] mb-2">
              Height
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                value={formData.heightValue}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    heightValue: e.target.value,
                  }));
                  validateField("heightValue", e.target.value);
                }}
                onBlur={(e) => validateField("heightValue", e.target.value)}
                placeholder="Feet/cm"
                className={`w-full px-[16px] py-[15px] text-[14px] text-[#202020] font-inter font-semibold border rounded-lg focus:outline-none focus:border-[#0DB5B5] ${
                  fieldErrors.heightValue
                    ? "border-red-500"
                    : "border-[#E0E0E0]"
                }`}
              />
              <div className="relative" ref={dropdownRefs.height}>
                <button
                  onClick={() => toggleDropdown("height")}
                  className="px-[16px] py-[15px] text-[14px] text-[#202020] font-inter font-semibold border border-[#E0E0E0] rounded-lg bg-white flex items-center gap-2 focus:outline-none focus:border-[#0DB5B5]"
                >
                  <span>{formData.heightUnit}</span>
                  {activeDropdown === "height" ? (
                    <ChevronUp className="w-2 h-2" />
                  ) : (
                    <ChevronDown className="w-2 h-2" />
                  )}
                </button>
                {activeDropdown === "height" && (
                  <div
                    className="absolute z-10 right-0 mt-1 bg-white rounded-lg"
                    style={{ boxShadow: "2px 4px 16px 0px #00000026" }}
                  >
                    <button
                      onClick={() => {
                        handleHeightUnitChange("ft");
                        closeDropdown();
                      }}
                      className="w-full px-3 py-2 text-left hover:bg-gray-50 rounded-t-lg"
                    >
                      ft
                    </button>
                    <button
                      onClick={() => {
                        handleHeightUnitChange("cm");
                        closeDropdown();
                      }}
                      className="w-full px-3 py-2 text-left hover:bg-gray-50 rounded-b-lg"
                    >
                      cm
                    </button>
                  </div>
                )}
              </div>
              {formData.heightUnit === "ft" && (
                <input
                  type="number"
                  value={formData.heightInches}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      heightInches: e.target.value,
                    }))
                  }
                  placeholder="Inches"
                  className="w-20 px-[16px] py-[15px] text-[14px] text-[#202020] font-inter font-semibold border border-[#E0E0E0] rounded-lg focus:outline-none focus:border-[#0DB5B5]"
                />
              )}
              {formData.heightUnit === "ft" && (
                <span className="px-[16px] py-[15px] text-[14px] text-[#202020] font-inter font-semibold border border-[#E0E0E0] rounded-lg bg-gray-50">
                  in
                </span>
              )}
            </div>
            {fieldErrors.heightValue && (
              <p className="text-red-500 text-xs mt-1">
                {fieldErrors.heightValue}
              </p>
            )}
          </div>

          {/* Weight */}
          <div className="mb-4">
            <label className="block text-[16px] font-semibold text-[#828282] mb-2">
              Weight
            </label>
            <div className="flex">
              <input
                type="number"
                value={formData.weightValue}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    weightValue: e.target.value,
                  }));
                  validateField("weightValue", e.target.value);
                }}
                onBlur={(e) => validateField("weightValue", e.target.value)}
                placeholder="Enter your weight"
                className={`w-full px-[24px] py-[15px] text-[14px] text-[#202020] font-inter font-semibold border rounded-lg focus:outline-none focus:border-[#0DB5B5] mr-2 ${
                  fieldErrors.weightValue
                    ? "border-red-500"
                    : "border-[#E0E0E0]"
                }`}
              />
              <div className="relative" ref={dropdownRefs.weight}>
                <button
                  onClick={() => toggleDropdown("weight")}
                  className="px-[16px] py-[15px] text-[14px] text-[#202020] font-inter font-semibold border border-[#E0E0E0] rounded-lg bg-white flex items-center gap-2 focus:outline-none focus:border-[#0DB5B5]"
                >
                  <span>{formData.weightUnit}</span>
                  {activeDropdown === "weight" ? (
                    <ChevronUp className="w-2 h-2" />
                  ) : (
                    <ChevronDown className="w-2 h-2" />
                  )}
                </button>
                {activeDropdown === "weight" && (
                  <div
                    className="absolute z-10 right-0 mt-2 bg-white rounded-lg"
                    style={{ boxShadow: "2px 4px 16px 0px #00000026" }}
                  >
                    <button
                      onClick={() => {
                        handleWeightUnitChange("kg");
                        closeDropdown();
                      }}
                      className="w-full px-3 py-2 text-left hover:bg-gray-50 rounded-t-lg"
                    >
                      kg
                    </button>
                    <button
                      onClick={() => {
                        handleWeightUnitChange("lbs");
                        closeDropdown();
                      }}
                      className="w-full px-3 py-2 text-left hover:bg-gray-50 rounded-b-lg"
                    >
                      lbs
                    </button>
                  </div>
                )}
              </div>
            </div>
            {fieldErrors.weightValue && (
              <p className="text-red-500 text-xs mt-1">
                {fieldErrors.weightValue}
              </p>
            )}
          </div>

          {/* Food Preference */}
          <div className="mb-4 relative" ref={dropdownRefs.food}>
            <label className="block text-[16px] font-semibold text-[#828282] mb-2">
              Food preference
            </label>
            <button
              onClick={() => toggleDropdown("food")}
              className="w-full p-3 border border-gray-300 rounded-lg bg-white font-inter text-left flex justify-between items-center focus:outline-none focus:border-[#0DB5B5]"
            >
              <span
                className={
                  formData.foodPreference ? "text-black" : "text-gray-400"
                }
              >
                {formData.foodPreference || "Select food preference"}
              </span>
              {activeDropdown === "food" ? (
                <ChevronUp className="w-2 h-2" />
              ) : (
                <ChevronDown className="w-2 h-2" />
              )}
            </button>
            {activeDropdown === "food" && (
              <div
                className="absolute z-10 w-full mt-2 bg-white rounded-lg max-h-60 overflow-y-auto"
                style={{ boxShadow: "2px 4px 16px 0px #00000026" }}
              >
                {foodOptions.map((option) => (
                  <button
                    key={option}
                    onClick={() => {
                      setFormData((prev) => ({
                        ...prev,
                        foodPreference: option,
                      }));
                      closeDropdown();
                    }}
                    className={`w-full p-3 font-inter text-left hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${
                      formData.foodPreference === option ? "text-[#0DB5B5]" : ""
                    }`}
                  >
                    {option}
                    {formData.foodPreference === option && (
                      <Check className="w-4 h-4 float-right mt-0.5" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Medical Conditions */}
          <div className="mb-4">
            <label className="block text-[16px] font-semibold text-[#828282] mb-2">
              Medical conditions
            </label>
            <div className="space-y-4 border border-[#CECECE] rounded-lg p-4">
              {medicalConditions.map((condition) => {
                const isSelected =
                  formData.medicalConditions.includes(condition);
                const hasDetails = conditionDetails[condition];

                return (
                  <div
                    key={condition}
                    className="flex items-center justify-between border-b last:border-b-0 pb-4 mt-2"
                  >
                    <label className="flex items-center flex-1">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleMedicalConditionToggle(condition)}
                        className="w-4 h-4 text-white bg-[#0DB5B5] border border-[#0DB5B5] rounded"
                      />
                      <div className="ml-3 flex-1">
                        <span className="text-sm font-medium font-inter block">
                          {condition}
                        </span>
                        {hasDetails && isSelected && (
                          <span className="text-xs text-[#646464] font-inter mt-1 block line-clamp-2">
                            {hasDetails.length > 50
                              ? `${hasDetails.substring(0, 50)}...`
                              : hasDetails}
                          </span>
                        )}
                      </div>
                    </label>
                    {condition !== "None" && (
                      <button
                        onClick={() => handleConditionEdit(condition)}
                        className="text-[#0DB5B5] ml-2 flex-shrink-0"
                      >
                        {isSelected ? (
                          <PenIcon className="w-4 h-4" />
                        ) : (
                          <Plus className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {formData.medicalConditions.length === 0 && (
              <p className="text-red-500 text-xs mt-1">
                Please select at least one medical condition
              </p>
            )}
          </div>

          {/* Goal */}
          <div className="mb-4">
            <label className="block text-[16px] font-semibold text-[#828282] mb-2">
              What help you are looking from us?
            </label>
            <textarea
              value={formData.goal}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, goal: e.target.value }));
                validateField("goal", e.target.value);
              }}
              onBlur={(e) => validateField("goal", e.target.value)}
              placeholder="Write goal"
              rows={4}
              className={`w-full px-[16px] py-[15px] text-[14px] text-[#202020] font-inter font-semibold bg-[#F6F7F7] rounded-[16px] focus:outline-none focus:border-[#0DB5B5] resize-none border ${
                fieldErrors.goal ? "border-red-500" : "border-transparent"
              }`}
            />
            {fieldErrors.goal && (
              <p className="text-red-500 text-xs mt-1">{fieldErrors.goal}</p>
            )}
          </div>

          {/* Medical Report Upload */}
          <div className="mb-4">
            <label className="block text-[16px] font-semibold text-[#828282] mb-2">
              Medical report (optional)
            </label>
            {formData.medicalReport ? (
              <div className="flex items-center justify-between p-3 border border-[#CECECE] rounded-lg bg-[#F9F9F9]">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-red-500 rounded flex items-center justify-center mr-3">
                    <span className="text-white text-xs font-bold">PDF</span>
                  </div>
                  <div>
                    <p className="text-[12px] font-semibold font-inter">
                      {formData.medicalReport.name}
                    </p>
                  </div>
                </div>
                <button onClick={removeFile} className="text-[#646464]">
                  <X className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <>
                <label
                  className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-[16px] cursor-pointer ${
                    fileUploadError
                      ? "border-red-400 bg-red-50"
                      : "border-[#CECECE] bg-gray-50 hover:bg-gray-100"
                  }`}
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <UploadIcon
                      className={`w-10 h-10 mb-3 ${
                        fileUploadError ? "text-red-500" : "text-[#0DB5B5]"
                      }`}
                    />
                    <p
                      className={`mb-1 text-[12px] ${
                        fileUploadError ? "text-red-500" : "text-[#0DB5B5]"
                      }`}
                    >
                      <span className="font-medium">Click to upload</span>
                    </p>
                    <p className="text-[10px] text-[#8E8E93]">
                      Max size file 5MB
                    </p>
                  </div>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
                {fileUploadError && (
                  <p className="text-red-500 text-xs mt-1 flex items-center">
                    <X className="w-4 h-4 mr-1" />
                    {fileUploadError}
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Fixed Bottom Button */}
        <div
          className="fixed bottom-0 left-0 right-0 p-4"
          style={{
            backdropFilter: "blur(10px)",
          }}
        >
          <button
            onClick={handleFormSubmit}
            disabled={!isFormValid() || isSubmitting}
            className={`w-full py-4 rounded-xl font-medium text-[16px] transition-colors duration-200 ${
              isFormValid() && !isSubmitting
                ? "bg-[#0DB5B5] text-white hover:bg-[#0A9999] cursor-pointer"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {isSubmitting ? "Sending..." : "Send"}
          </button>
        </div>

        {/* Medical Condition Popup */}
        <MedicalConditionPopup
          isOpen={showMedicalPopup}
          onClose={handleCloseMedicalPopup}
          condition={selectedCondition}
          onSave={handleConditionDetailsSave}
          initialDetails={conditionDetails[selectedCondition] || ""}
        />
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-white px-[24px] mt-[32px]">
        {/* Header */}
        <div className="flex items-center bg-white mb-4">
          <button onClick={onBack} className="flex items-center gap-1">
            <ChevronLeft className="w-6 h-6 text-[#173430]" />
            <span className="text-[14px] font-medium text-[#202020]">Back</span>
          </button>
        </div>

        <div className="pb-24">
          {/* Premium Signature Plan Card */}
          <div
            className="border-[0.5px] border-[#0DB5B5] rounded-[12px] mb-6 bg-[#0DB5B5]"
            style={{ boxShadow: "2px 4px 16px 0px #00000026" }}
          >
            {/* Know More Button */}
            <button
              onClick={handleKnowMore}
              className="w-full text-white py-3 px-4 font-medium font-inter flex items-center justify-center text-[12px]"
            >
              Know more about Deepika Chalasani
              <TrendingUp className="w-4 h-4 ml-2" />
            </button>
            <div className="rounded-[12px] p-5 bg-[#FAFFFF]">
              {/* Premium Tag */}
              <div className="flex items-center mb-3">
                <GradientBorderPremiumTag>
                  <Star className="w-4 h-4 mr-1 text-[#003E3E]" />
                  Premium
                </GradientBorderPremiumTag>
              </div>

              {/* Title & Description */}
              <h2 className="text-[18px] font-inter font-semibold text-[#202020] mb-[2px]">
                Triva Signature
              </h2>
              <p className="text-[10px] text-[#646464] font-inter font-normal mb-2">
                Triva App + High Touch Real-Time Support + Expert Guidance by Deepika Chalasani
              </p>
            </div>
          </div>

          {/* Summary */}
          <div className="mb-4">
            <p className="text-[12px] text-[#202020] font-medium font-inter leading-relaxed">
              A premium transformation program designed for those who prefer personalized guidance combined with technology
            </p>
          </div>

          {/* Perfect For */}
          <div className="mb-4">
            <h2 className="text-[14px] font-semibold text-[#202020] mb-3">
              Perfect for:
            </h2>
            <div className="flex flex-wrap gap-2">
              {perfectFor.map((item, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-[#F1FBFB] text-[#003E3E] rounded-full text-[12px] font-normal border-[0.5px] border-[#6DD0CF]"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>

          {/* What You Get */}
          <div className="mb-4">
            <h2 className="text-[14px] font-semibold text-[#202020] mb-2">
              What you get:
            </h2>
            <div className="space-y-2">
              {whatYouGet.map((item, index) => (
                <div key={index} className="flex items-start">
                  <Check className="w-3 h-3 text-[#003E3E] mr-2 mt-1.5 flex-shrink-0" />
                  <span className="text-[#202020] text-sm leading-relaxed">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* Fixed Bottom Button */}
        <div
          className="fixed bottom-0 left-0 right-0 p-4"
          style={{
            backdropFilter: "blur(10px)",
          }}
        >
          <button
            onClick={handleConnectWithExperts}
            className="w-full bg-[#0DB5B5] text-white py-4 rounded-xl font-medium text-[16px] transition-colors duration-200"
          >
            Connect with experts
          </button>
        </div>
      </div>
    </>
  );
};

export default PremiumSignaturePlanDetails;
